"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { signIn, useSession } from "next-auth/react";
import { useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";

import { cn, errorIntoString, sanitizeUserFacingErrorMessage } from "@/lib/utils";
import { OAUTH_APPLE_ENDPOINT, OAUTH_GOOGLE_ENDPOINT } from "@/lib/constant";
import { rememberDetectedCountryForAuth } from "@/lib/ip-geolocation";
import { emailSignInSchema, EmailSignInSchema } from "@/lib/schemas/email-signin";
import { useSafeReCaptcha } from "@/hooks/useSafeReCaptcha";
import {
	AUTH_CARD_HEADER_TOP_LOGIN_PX,
	AUTH_LOGIN_CARD_DESCRIPTION,
} from "@/lib/auth-card-copy";
import { getSafePostAuthRedirectUrl } from "@/lib/utils/returnUrl";
import { getClientFrontendOrigin } from "@/lib/utils/frontend-origin";
import { ga4CtaClick, ga4Login } from "@/lib/analytics/ga4-ecommerce";

import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import TextInput from "@/components/form/TextInput";
import { useToast } from "@/components/ui/use-toast";
import PasswordInput from "@/components/form/PasswordInput";
import AuthProviderButton from "@/components/AuthProviderButton";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import AppleIcon from "@mui/icons-material/Apple";

import googleIcon from "/public/assets/login/login-form/google.png";

export type LoginCardProps = {
	variant?: "page" | "modal";
	/** When `variant` is `modal`, used instead of URL query for post-login navigation */
	returnUrlOverride?: string;
	onSwitchToSignup?: () => void;
	onAuthenticated?: () => void | Promise<void>;
	hideTitle?: boolean;
	allowAuthenticatedLogin?: boolean;
};

export default function LoginCard({
	variant = "page",
	returnUrlOverride = "/profile/order-history",
	onSwitchToSignup,
	onAuthenticated,
	hideTitle = false,
	allowAuthenticatedLogin = false,
}: LoginCardProps) {
	const router = useRouter();
	const { toast } = useToast();
	const { data: session, status } = useSession();
	const { executeRecaptcha } = useSafeReCaptcha();
	const [requires2FA, setRequires2FA] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const form = useForm<EmailSignInSchema>({
		resolver: zodResolver(emailSignInSchema),
		defaultValues: {
			email: "",
			keepMeSignedIn: false,
			twoFactorToken: "",
		},
	});

	const effectiveReturnUrl = (): string => {
		if (variant === "modal") {
			return returnUrlOverride;
		}
		const searchParams = new URLSearchParams(window.location.search);
		return searchParams.get("returnUrl") || searchParams.get("callbackUrl") || "/";
	};

	// Redirect authenticated users (page: full redirect; modal: navigate to return URL)
	useEffect(() => {
		if (allowAuthenticatedLogin) return;
		if (status !== "authenticated" || !session) return;

		const searchParams = new URLSearchParams(window.location.search);
		const returnUrl =
			variant === "modal"
				? returnUrlOverride
				: searchParams.get("returnUrl") || searchParams.get("callbackUrl") || "/";

		console.log("[LoginCard] User is already authenticated");

		const safeUrl = getSafePostAuthRedirectUrl(returnUrl);
		console.log("[LoginCard] Redirecting to:", safeUrl);
		if (variant === "modal" && onAuthenticated) {
			void onAuthenticated();
		}
		if (safeUrl.startsWith("http")) {
			window.location.href = safeUrl;
		} else {
			router.replace(safeUrl);
		}
	}, [allowAuthenticatedLogin, status, session, router, variant, returnUrlOverride, toast, onAuthenticated]);

	if (status === "authenticated" && !allowAuthenticatedLogin) {
		return null;
	}

	const onSubmit = async (values: EmailSignInSchema) => {
		setIsLoading(true);
		try {
			let captchaToken: string | undefined;
			if (executeRecaptcha) {
				try {
					captchaToken = await executeRecaptcha("login");
				} catch (error) {
					console.error("reCAPTCHA error:", error);
					if (process.env.NODE_ENV === "production") {
						toast({
							title: "Security verification failed",
							description: "Please try again.",
							variant: "destructive",
						});
						setIsLoading(false);
						return;
					}
				}
			}

			if (process.env.NODE_ENV === "production" && !captchaToken) {
				toast({
					title: "Security verification failed",
					description: "Please refresh the page and try again.",
					variant: "destructive",
				});
				setIsLoading(false);
				return;
			}

			const requestBody: {
				email: string;
				password: string;
				twoFactorToken?: string;
				captchaToken?: string;
				clientType: "web";
			} = {
				email: values.email,
				password: values.password,
				clientType: "web",
			};

			if (values.twoFactorToken && values.twoFactorToken.trim().length > 0) {
				requestBody.twoFactorToken = values.twoFactorToken.trim();
			}

			if (captchaToken) {
				requestBody.captchaToken = captchaToken;
			}

			const response = await axios.post<{ id_token?: string; requires2FA?: boolean }>(
				`${process.env.NEXT_PUBLIC_BACKEND_URL || "https://api.techxeed.com"}/auth/signin`,
				requestBody,
				{
					headers: {
						"X-Requested-With": "XMLHttpRequest",
					},
				},
			);

			console.log("Login API response:", JSON.stringify(response.data, null, 2));

			const responseData = response.data;
			const needs2FA = (responseData as { requires2FA?: boolean })?.requires2FA === true;

			if (needs2FA && !values.twoFactorToken) {
				console.log("2FA required, showing input field");
				setRequires2FA(true);
				setIsLoading(false);
				toast({
					title: "2FA Required",
					description: "Please enter your authentication code from your authenticator app.",
				});
				return;
			}

			if (response.data && response.data.id_token) {
				let nextAuthCaptchaToken: string | undefined;
				if (executeRecaptcha) {
					nextAuthCaptchaToken = await executeRecaptcha("login");
				}

				if (process.env.NODE_ENV === "production" && !nextAuthCaptchaToken) {
					setIsLoading(false);
					return toast({
						title: "Security verification failed",
						description: "Please refresh the page and try again.",
						variant: "destructive",
					});
				}

				const result = await signIn("credentials", {
					redirect: false,
					email: values.email,
					password: values.password,
					twoFactorToken: values.twoFactorToken,
					captchaToken: nextAuthCaptchaToken,
					clientType: "web",
				});

				if (!result?.ok) {
					const ex = errorIntoString(result?.error ?? "Unknown Error");
					setIsLoading(false);
					return toast({
						variant: "destructive",
						title: "Failed to complete sign-in",
						description: ex,
					});
				}

				ga4Login("email");

				setIsLoading(false);

				if (variant === "modal") {
					await onAuthenticated?.();
					return;
				}

				let returnUrl = effectiveReturnUrl();

				const safeUrl = getSafePostAuthRedirectUrl(returnUrl);
				if (safeUrl.startsWith("http")) {
					window.location.href = safeUrl;
				} else {
					router.push(safeUrl);
					router.refresh();
				}
			} else {
				setIsLoading(false);
				toast({
					variant: "destructive",
					title: "Login failed",
					description: "Login could not be completed. Please try again.",
				});
			}
		} catch (error: unknown) {
			setIsLoading(false);

			const err = error as {
				response?: { data?: { message?: string; requires2FA?: boolean } };
				message?: string;
			};

			if (
				err?.response?.data &&
				"requires2FA" in err.response.data &&
				err.response.data.requires2FA === true
			) {
				setRequires2FA(true);
				toast({
					title: "2FA Required",
					description: "Please enter your authentication code from your authenticator app.",
				});
				return;
			}

			const errorMessage = sanitizeUserFacingErrorMessage(
				err?.response?.data?.message || err?.message,
				"Login failed",
			);

			if (errorMessage && errorMessage.includes("connected to Google")) {
				toast({
					variant: "destructive",
					title: "Google Account Connected",
					description: errorMessage,
				});
				return;
			}

			if (errorMessage && errorMessage.includes("Invalid 2FA token")) {
				setRequires2FA(true);
				form.setValue("twoFactorToken", "");
				toast({
					variant: "destructive",
					title: "Invalid 2FA Code",
					description:
						"The authentication code you entered is incorrect or has expired. Please enter a new code from your authenticator app.",
				});
				return;
			}

			if (process.env.NODE_ENV === "development") {
				console.error("Login error:", error);
				console.error("Error response data:", err?.response?.data);
			}

			toast({
				variant: "destructive",
				title: "Login failed",
				description: errorMessage,
			});
		}
	};

	const handleGoogleAuth = async () => {
		const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

		if (!clientId || clientId.includes("REPLACE") || clientId.includes("REPLACE_WITH")) {
			console.warn("Google Client ID not configured. Google OAuth is disabled for local development.");
			toast({
				title: "Google OAuth Not Available",
				description: "Google OAuth is not configured for local development. Please use email/password login.",
				variant: "default",
			});
			return;
		}

		ga4CtaClick({
			cta_id: "login_google",
			cta_name: "Continue with Google",
			cta_location: "login_card",
		});

		const baseUrl = getClientFrontendOrigin();
		const redirectUri = `${baseUrl}/login/google`;
		const oauthState = variant === "modal" ? returnUrlOverride : effectiveReturnUrl();

		const searchParams = new URLSearchParams({
			client_id: clientId,
			redirect_uri: redirectUri,
			response_type: "code",
			scope: "email profile",
			include_granted_scopes: "true",
			state: oauthState,
		});

		const oauthUrl = `${OAUTH_GOOGLE_ENDPOINT}?${searchParams.toString()}`;

		await rememberDetectedCountryForAuth();
		setTimeout(() => {
			window.location.replace(oauthUrl);
		}, 100);
	};

	const handleAppleAuth = async () => {
		const clientId = process.env.NEXT_PUBLIC_APPLE_CLIENT_ID;

		if (!clientId || clientId.includes("REPLACE") || clientId.includes("REPLACE_WITH")) {
			toast({
				title: "Apple Sign In not available",
				description: "Apple Sign In is not configured. Use email/password or Google, or contact support.",
				variant: "default",
			});
			return;
		}

		ga4CtaClick({
			cta_id: "login_apple",
			cta_name: "Continue with Apple",
			cta_location: "login_card",
		});

		const baseUrl = getClientFrontendOrigin();
		const redirectUri = `${baseUrl}/login/apple`;
		const oauthState = variant === "modal" ? returnUrlOverride : effectiveReturnUrl();

		const searchParams = new URLSearchParams({
			client_id: clientId,
			redirect_uri: redirectUri,
			response_type: "code id_token",
			response_mode: "form_post",
			scope: "name email",
			state: oauthState,
		});

		const oauthUrl = `${OAUTH_APPLE_ENDPOINT}?${searchParams.toString()}`;
		await rememberDetectedCountryForAuth();
		window.location.replace(oauthUrl);
	};

	const signUpControl =
		variant === "modal" && onSwitchToSignup ? (
			<button
				type="button"
				onClick={() => {
					ga4CtaClick({
						cta_id: "signup_from_login",
						cta_name: "Sign Up",
						cta_location: "login_card",
					});
					onSwitchToSignup();
				}}
				className="text-xxsm font-normal text-accent-foreground underline"
			>
				Sign up
			</button>
		) : (
			<Link
				className="text-xxsm font-normal text-accent-foreground underline"
				href="/signup"
				onClick={() =>
					ga4CtaClick({
						cta_id: "signup_from_login",
						cta_name: "Sign Up",
						cta_location: "login_card",
						link_url: "/signup",
					})
				}
			>
				Sign up
			</Link>
		);

	return (
		<Card className="w-full max-w-md rounded-3xl border-none px-1 !shadow-custom sm:px-6">
			<CardHeader className="flex flex-col gap-[14px] space-y-0 px-8 pb-0 pt-0">
				{hideTitle ? null : (
					<CardTitle
						className="text-xl font-semibold leading-snug xs:text-2xl sm:text-2xl md:text-3xl"
						style={{ paddingTop: AUTH_CARD_HEADER_TOP_LOGIN_PX }}
					>
						Welcome back! <br />
						Let&apos;s <span className="text-accent-foreground ">get started</span>
					</CardTitle>
				)}
				<CardDescription className={cn("mt-0 pt-0", hideTitle && "pt-8 text-center text-sm font-medium")}>
					{AUTH_LOGIN_CARD_DESCRIPTION}
				</CardDescription>
			</CardHeader>
			<CardContent className="px-8 pb-0 pt-[20px]">
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="flex flex-col justify-center pt-0"
					>
						<TextInput
							name="email"
							label="Email"
							placeholder="Email"
							autoComplete="email"
							disabled={requires2FA}
						/>
						<PasswordInput
							name="password"
							label="Password"
							placeholder="Password"
							autoComplete="current-password"
							disabled={requires2FA}
						/>

						{requires2FA && (
							<div className="mt-4">
								<TextInput
									name="twoFactorToken"
									label="Authentication Code"
									placeholder="Enter 6-digit code from your authenticator app"
									autoComplete="one-time-code"
								/>
								<p className="mt-2 text-xs text-muted-foreground">
									Enter the 6-digit code from your authenticator app to complete login.
								</p>
							</div>
						)}

						<div className="flex items-center justify-end pt-1">
							<Link
								href="/login/password-reset"
								className="flex items-center justify-center text-xxsm font-normal text-muted-foreground"
							>
								Forgotten password?
							</Link>
						</div>

						<Button
							className="mx-4 mt-6 h-12 text-xs shadow-custom sm:mx-6"
							type="submit"
							disabled={isLoading}
						>
							{isLoading ? "Logging in..." : requires2FA ? "Verify & Login" : "Login"}
						</Button>
					</form>
				</Form>

				<div className="mt-4 flex w-full items-center">
					<div className="border-visible grow border-t" />
					<span className="mx-4 shrink text-xs text-muted-foreground">or</span>
					<div className="border-visible grow border-t" />
				</div>

				<div className="flex flex-col gap-y-4 py-4">
					<AuthProviderButton
						title="Google"
						logo={googleIcon}
						onClick={handleGoogleAuth}
						disabled={
							!process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ||
							process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID.includes("REPLACE")
						}
					/>
					<AuthProviderButton
						title="Apple"
						icon={
							<AppleIcon
								className="text-black dark:!text-white"
								style={{ fontSize: 24 }}
								aria-hidden
								inheritViewBox
							/>
						}
						onClick={handleAppleAuth}
						disabled={
							!process.env.NEXT_PUBLIC_APPLE_CLIENT_ID ||
							process.env.NEXT_PUBLIC_APPLE_CLIENT_ID.includes("REPLACE") ||
							process.env.NEXT_PUBLIC_APPLE_CLIENT_ID.includes("REPLACE_WITH")
						}
					/>
				</div>
			</CardContent>

			<CardFooter className="flex justify-center gap-1 py-8 text-center">
				<span className="text-xxsm font-normal text-muted-foreground">Don&apos;t have an account? </span>
				{signUpControl}
			</CardFooter>
		</Card>
	);
}
